/**
 * Returns the origin to use when building shareable quote URLs.
 *
 * Production: VITE_APP_URL is the single source of truth (baked in at build
 * time — see `artifacts/quoteds-arrow/.replit-artifact/artifact.toml`). If it
 * is missing in a production build we log a loud error and fall back to
 * window.location.origin so sharing keeps working, but the misconfiguration
 * needs to be fixed.
 *
 * Development: VITE_APP_URL is optional. When absent we use
 * window.location.origin so share links on a `.replit.dev` preview stay on
 * that preview.
 */
export function getShareOrigin(): string {
  const appUrl = import.meta.env.VITE_APP_URL;
  if (appUrl && typeof appUrl === "string" && appUrl.trim()) {
    return appUrl.trim().replace(/\/$/, "");
  }
  if (import.meta.env.PROD) {
    // eslint-disable-next-line no-console
    console.error(
      "[share] VITE_APP_URL is not set in a production build. Share links may " +
        "point at the wrong host. Set it in the artifact.toml production " +
        "buildEnv (e.g. https://quotedsarrow.com).",
    );
  }
  return window.location.origin;
}

/**
 * Returns a fully-qualified share URL for a quote.
 *
 * The URL points to the server-side OG preview endpoint at
 * /api/og/quotes/:id. Because that path lives on the same origin as the SPA
 * (the API is mounted under /api on the same public domain), the URL is
 * derived from VITE_APP_URL in production and from window.location.origin in
 * dev. Bots that fetch this URL receive a page with per-quote Open Graph
 * meta tags and a 1200×630 dynamic image; real users are immediately
 * redirected to the SPA's quote page.
 *
 * When `referralCode` is provided (signed-in user sharing a quote), it is
 * appended as `?ref=CODE` so any resulting signup is attributed to the sharer.
 *
 * Env vars:
 *   VITE_APP_URL — public URL of the deployed app (e.g. https://quotedsarrow.com).
 *                  Required in production. Optional in dev (falls back to
 *                  window.location.origin).
 */
export function getOgShareUrl(quoteId: number, referralCode?: string | null): string {
  const base = getShareOrigin();
  const url = `${base}/api/og/quotes/${quoteId}`;
  return referralCode ? `${url}?ref=${encodeURIComponent(referralCode)}` : url;
}
